import { useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/chalkApi";
import { copyToClipboard } from "../../lib/copyToClipboard";
import { BottomSheet, ChalkActionButton } from "./primitives";

const LABELS = ["Team Mom", "Team Dad", "Team Coordinator", "Assistant Coach"];

export function InviteStaffSheet({ isOpen, teamId, onDismiss, onInvited }) {
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("Team Mom");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  function close() {
    setEmail(""); setLink(""); setLabel("Team Mom");
    onDismiss();
  }

  async function send() {
    setBusy(true);
    try {
      const d = await api.inviteStaff(teamId, { email, label });
      setLink(`${window.location.origin}${d.invite_path}`);
      toast.success(`Invite sent to ${d.email}.`);
      onInvited();
    } catch (e) {
      toast.error(e.message || "Couldn't send that invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Invite team staff" onDismiss={close}>
      {link ? (
        <div data-testid="staff-invite-sent">
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--chalk-board)" }}>
            We emailed the invite. You can also text them this link (works for 7 days, only for that email):
          </p>
          <div data-testid="staff-invite-link" style={{ fontSize: 12, wordBreak: "break-all", padding: 10, borderRadius: 8, background: "rgba(46,125,82,0.08)", margin: "8px 0 14px" }}>{link}</div>
          <ChalkActionButton testId="staff-invite-copy" label="Copy link" icon={<Copy size={15} />}
            onClick={async () => toast[(await copyToClipboard(link)) ? "success" : "error"]("Link copied.")} />
          <div style={{ height: 8 }} />
          <ChalkActionButton testId="staff-invite-done" variant="ghost" label="Done" onClick={close} />
        </div>
      ) : (
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)" }} htmlFor="staff-invite-email">Their email</label>
          <input id="staff-invite-email" type="email" inputMode="email" autoComplete="off" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="staff-invite-email"
            style={{ width: "100%", fontSize: 16, padding: "12px 14px", borderRadius: 10, border: "0.5px solid var(--chalk-border)", margin: "6px 0 14px", boxSizing: "border-box" }} />
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>Their role</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {LABELS.map((l) => (
              <button key={l} type="button" aria-pressed={label === l} onClick={() => setLabel(l)} data-testid={`staff-invite-label-${l.toLowerCase().replace(/ /g, "-")}`}
                style={{ padding: "8px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", outline: "none",
                  background: label === l ? "var(--green-mid)" : "#fff", color: label === l ? "#fff" : "#1F3D2B",
                  border: label === l ? "1px solid var(--green-mid)" : "1px solid rgba(0,0,0,0.15)", transition: "background-color 150ms ease" }}>{l}</button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "var(--chalk-muted)", lineHeight: 1.5, marginBottom: 14 }}>
            {label === "Assistant Coach"
              ? "Assistant coaches start with roster, events, cancel/delay, Chalk Talk and volunteers."
              : `A ${label} starts with Chalk Talk posting and volunteer management. Roster editing is off.`}{" "}
            You can change this any time. Medical notes and parent emails stay private until you turn them on.
          </p>
          <ChalkActionButton testId="staff-invite-send" label="Send invite" loading={busy} disabled={!email.includes("@")} onClick={send} />
        </div>
      )}
    </BottomSheet>
  );
}
